import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Suspense, lazy, type ReactNode } from 'react'
import { isNative } from './lib/native'

/**
 * 네이티브 앱 셸 — edge-to-edge(Android 15+) 대응.
 * 상단(상태바)·하단(제스처바) inset을 스크롤 밖 고정 영역으로 두고,
 * 스크롤은 가운데 영역에서만 일어나게 한다 → 콘텐츠가 시스템 바와 겹치지 않음.
 * 웹/데스크톱에서는 셸 없이 기존 문서 스크롤 그대로.
 */
function AppShell({ children }: { children: ReactNode }) {
  if (!isNative()) return <>{children}</>
  // 상/하단 시스템바 영역은 검게(#000) — 스크롤 밖 고정. 가운데만 크림 콘텐츠 스크롤.
  return (
    <div style={{ height: '100dvh', display: 'flex', flexDirection: 'column', background: '#000' }}>
      <div style={{ height: 'env(safe-area-inset-top)', flexShrink: 0, background: '#000' }} />
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          background: '#edefeb',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
        }}
      >
        {children}
      </div>
      <div style={{ height: 'env(safe-area-inset-bottom)', flexShrink: 0, background: '#000' }} />
    </div>
  )
}

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ background: '#edefeb' }}>
    <div className="w-10 h-10 border-4 rounded-full animate-spin" style={{ borderColor: '#e8dcc4', borderTopColor: '#c0342b' }} />
  </div>
)

// 메인 섹션 (탭 레이아웃)
const JourneyHomePage = lazy(() => import('./components/pages/JourneyHomePage'))
const ExplorePage = lazy(() => import('./components/pages/ExplorePage'))
const ReviewPage = lazy(() => import('./components/pages/ReviewPage'))
const ProfilePage = lazy(() => import('./components/pages/ProfilePage'))
const CultureBridgePage = lazy(() => import('./components/pages/CultureBridgePage'))
const CoursesPage = lazy(() => import('./components/pages/CoursesPage'))
const LeaderboardPage = lazy(() => import('./components/pages/LeaderboardPage'))
const StatsDashboardPage = lazy(() => import('./components/pages/StatsDashboardPage'))

// 전체화면 (탭 없음)
const LandingPage = lazy(() => import('./components/pages/LandingPage'))
const DownloadPage = lazy(() => import('./components/pages/DownloadPage'))
const OnboardingFlowPage = lazy(() => import('./components/pages/OnboardingFlowPage'))
const UnitLessonPage = lazy(() => import('./components/pages/UnitLessonPage'))
const MockExamPage = lazy(() => import('./components/pages/MockExamPage'))
const PremiumPage = lazy(() => import('./components/pages/PremiumPage'))
const CalculatorPage = lazy(() => import('./components/pages/CalculatorPage'))
const ExamReportPage = lazy(() => import('./components/pages/ExamReportPage'))
const AvatarChatPage = lazy(() => import('./components/pages/AvatarChatPage'))
const CultureBridgeTopicPage = lazy(() => import('./components/pages/CultureBridgeTopicPage'))
const CourseDetailPage = lazy(() => import('./components/pages/CourseDetailPage'))
const LessonPage = lazy(() => import('./components/pages/LessonPage'))
const HangulLessonV2Page = lazy(() => import('./components/pages/HangulLessonV2Page'))
const SettingsPage = lazy(() => import('./components/pages/SettingsPage'))
const SubscriptionPage = lazy(() => import('./components/pages/SubscriptionPage'))
const LoginPage = lazy(() => import('./components/pages/LoginPage'))
const PrivacyPolicyPage = lazy(() => import('./components/pages/PrivacyPolicyPage'))
const TermsPage = lazy(() => import('./components/pages/TermsPage'))
const DeleteAccountPage = lazy(() => import('./components/pages/DeleteAccountPage'))
const KoreanExplorePage = lazy(() => import('./components/pages/explore/KoreanExplorePage'))
const ProverbsPage = lazy(() => import('./components/pages/explore/ProverbsPage'))
const SlangPage = lazy(() => import('./components/pages/explore/SlangPage'))
const PhrasesPage = lazy(() => import('./components/pages/explore/PhrasesPage'))
const FolkSongPage = lazy(() => import('./components/pages/explore/FolkSongPage'))
const SeasonsPage = lazy(() => import('./components/pages/explore/SeasonsPage'))
const SeasonDetailPage = lazy(() => import('./components/pages/explore/SeasonDetailPage'))
const LifePage = lazy(() => import('./components/pages/explore/LifePage'))
const LifeDetailPage = lazy(() => import('./components/pages/explore/LifeDetailPage'))
const HeartPage = lazy(() => import('./components/pages/explore/HeartPage'))
const HeartDetailPage = lazy(() => import('./components/pages/explore/HeartDetailPage'))
const SensesPage = lazy(() => import('./components/pages/explore/SensesPage'))
const SenseDetailPage = lazy(() => import('./components/pages/explore/SenseDetailPage'))

// 관리자 콘솔 (KoreanFlow 이식) — /admin, 커스텀 클레임 게이팅
const AdminAuthGuard = lazy(() => import('./admin/components/AdminAuthGuard'))
const AdminLayout = lazy(() => import('./admin/components/AdminLayout'))
const AdminDashboardPage = lazy(() => import('./admin/pages/AdminDashboardPage'))
const AdminUsersPage = lazy(() => import('./admin/pages/AdminUsersPage'))
const AdminUserDetailPage = lazy(() => import('./admin/pages/AdminUserDetailPage'))
const AdminPartnersPage = lazy(() => import('./admin/pages/AdminPartnersPage'))
const AdminTransactionsPage = lazy(() => import('./admin/pages/AdminTransactionsPage'))
const AdminSettlementsPage = lazy(() => import('./admin/pages/AdminSettlementsPage'))
const AdminSettlementDetailPage = lazy(() => import('./admin/pages/AdminSettlementDetailPage'))
const AdminTicketsPage = lazy(() => import('./admin/pages/AdminTicketsPage'))
const AdminTicketDetailPage = lazy(() => import('./admin/pages/AdminTicketDetailPage'))
const AdminAnnouncementsPage = lazy(() => import('./admin/pages/AdminAnnouncementsPage'))
const AdminContentPage = lazy(() => import('./admin/pages/AdminContentPage'))
const AdminConfigPage = lazy(() => import('./admin/pages/AdminConfigPage'))
const AdminTargetsPage = lazy(() => import('./admin/pages/AdminTargetsPage'))
const AdminReservePage = lazy(() => import('./admin/pages/AdminReservePage'))
const AdminGatesPage = lazy(() => import('./admin/pages/AdminGatesPage'))
const AdminInvestorsPage = lazy(() => import('./admin/pages/AdminInvestorsPage'))
const AdminAuditLogPage = lazy(() => import('./admin/pages/AdminAuditLogPage'))

import TabLayout from './components/common/TabLayout'

function App() {
  return (
    <BrowserRouter>
      <AppShell>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* 마케팅 랜딩 — 항상 / (앱 진입은 헤더 CTA로). 앱 다운로드 안내는 /download */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/download" element={<DownloadPage />} />
          <Route path="/premium" element={<PremiumPage />} />
          <Route path="/calculator" element={<CalculatorPage />} />

          {/* 온보딩 (전체화면) */}
          <Route path="/onboarding-flow" element={<OnboardingFlowPage />} />

          {/* 메인 섹션 — 하단 5탭 네비 */}
          <Route element={<TabLayout />}>
            <Route path="/journey" element={<JourneyHomePage />} />
            <Route path="/explore" element={<ExplorePage />} />
            <Route path="/review" element={<ReviewPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/culture-bridge" element={<CultureBridgePage />} />
            <Route path="/courses" element={<CoursesPage />} />
            <Route path="/leaderboard" element={<LeaderboardPage />} />
            <Route path="/stats" element={<StatsDashboardPage />} />
          </Route>

          {/* 전체화면 (탭 없음) */}
          <Route path="/journey/unit/:packId/:unitId" element={<UnitLessonPage />} />
          <Route path="/journey/mock" element={<MockExamPage />} />
          <Route path="/journey/report" element={<ExamReportPage />} />
          <Route path="/chat" element={<AvatarChatPage />} />
          <Route path="/culture-bridge/:topicId" element={<CultureBridgeTopicPage />} />
          <Route path="/courses/:slug" element={<CourseDetailPage />} />
          <Route path="/lesson/:lessonId" element={<LessonPage />} />
          <Route path="/hangul-v2/:id" element={<HangulLessonV2Page />} />

          {/* 한국어 탐색 콘텐츠 (KoreanFlow 이식) */}
          <Route path="/explore/korean" element={<KoreanExplorePage />} />
          <Route path="/explore/korean/proverbs" element={<ProverbsPage />} />
          <Route path="/explore/korean/slang" element={<SlangPage />} />
          <Route path="/explore/korean/phrases" element={<PhrasesPage />} />
          <Route path="/explore/korean/folksongs" element={<FolkSongPage />} />
          <Route path="/explore/korean/seasons" element={<SeasonsPage />} />
          <Route path="/explore/korean/seasons/:id" element={<SeasonDetailPage />} />
          <Route path="/explore/korean/life" element={<LifePage />} />
          <Route path="/explore/korean/life/:sectionId" element={<LifeDetailPage />} />
          <Route path="/explore/korean/heart" element={<HeartPage />} />
          <Route path="/explore/korean/heart/:id" element={<HeartDetailPage />} />
          <Route path="/explore/korean/senses" element={<SensesPage />} />
          <Route path="/explore/korean/senses/:senseId" element={<SenseDetailPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/subscription" element={<SubscriptionPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/delete-account" element={<DeleteAccountPage />} />

          {/* 관리자 콘솔 — /admin (커스텀 클레임 superadmin/partner_admin 게이팅). KoreanFlow 이식 전체. */}
          <Route path="/admin" element={<AdminAuthGuard><AdminLayout /></AdminAuthGuard>}>
            <Route index element={<AdminDashboardPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="users/:userId" element={<AdminUserDetailPage />} />
            <Route path="partners" element={<AdminPartnersPage />} />
            <Route path="transactions" element={<AdminTransactionsPage />} />
            <Route path="settlements" element={<AdminSettlementsPage />} />
            <Route path="settlements/:id" element={<AdminSettlementDetailPage />} />
            <Route path="tickets" element={<AdminTicketsPage />} />
            <Route path="tickets/:id" element={<AdminTicketDetailPage />} />
            <Route path="announcements" element={<AdminAnnouncementsPage />} />
            <Route path="content" element={<AdminContentPage />} />
            <Route path="config" element={<AdminConfigPage />} />
            <Route path="targets" element={<AdminTargetsPage />} />
            <Route path="reserve" element={<AdminReservePage />} />
            <Route path="gates" element={<AdminGatesPage />} />
            <Route path="investors" element={<AdminInvestorsPage />} />
            <Route path="audit-log" element={<AdminAuditLogPage />} />
          </Route>

          {/* 레거시 은퇴 — 리다이렉트 */}
          <Route path="/dashboard" element={<Navigate to="/journey" replace />} />
          <Route path="/home" element={<Navigate to="/journey" replace />} />
          <Route path="/learn" element={<Navigate to="/explore" replace />} />
          <Route path="/onboarding" element={<Navigate to="/onboarding-flow" replace />} />
          <Route path="/why-demo" element={<Navigate to="/onboarding-flow" replace />} />
          <Route path="/start" element={<Navigate to="/onboarding-flow" replace />} />
          <Route path="/readiness-demo" element={<Navigate to="/journey" replace />} />
          {/* 은퇴한 중복·유령 경로 → 코히런트한 곳으로 */}
          <Route path="/course/:courseId" element={<Navigate to="/courses" replace />} />
          <Route path="/level-test" element={<Navigate to="/onboarding-flow" replace />} />
          <Route path="/family" element={<Navigate to="/profile" replace />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      </AppShell>
    </BrowserRouter>
  )
}

export default App
